import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/client";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { User, Mail, ArrowLeft, Save } from "lucide-react";
import Loader from "../../components/ui/Loader";

export default function AdminProfileEdit() {
 const [formData, setFormData] = useState({
 email: "",
 fullName: "",
 });
 const [user, setUser] = useState(null);
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [error, setError] = useState("");
 const [success, setSuccess] = useState("");
 const navigate = useNavigate();

 // Check if user logged in via Telegram
 const isTelegramUser = user?.telegramId || window.Telegram?.WebApp?.initDataUnsafe?.user;

 useEffect(() => {
 const fetchProfile = async () => {
 try {
 const res = await api.get("/auth/me");
 if (res.data && res.data.user) {
 const { email, fullName } = res.data.user;
 setUser(res.data.user);
 setFormData({ email, fullName: fullName || "" });
 }
 } catch (err) {
 console.error("Failed to fetch admin profile:", err);
 setError("Failed to load profile data.");
 } finally {
 setLoading(false);
 }
 };
 fetchProfile();
 }, []);

 const handleSubmit = async (e) => {
 e.preventDefault();
 setSaving(true);
 setError("");
 setSuccess("");

 try {
 await api.patch("/auth/me", formData);
 setSuccess("Profile updated successfully!");
 setTimeout(() => navigate("/admin/profile"), 1500);
 } catch (err) {
 console.error("Failed to update profile:", err);
 setError(err.response?.data?.message || "Failed to update profile.");
 } finally {
 setSaving(false);
 }
 };

 if (loading) return <Loader />;

 return (
 <div className="min-h-screen py-6 md:py-10">
 <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
 {/* Header */}
 <div className="mb-8">
 <Button
 variant="outline"
 onClick={() => navigate("/admin/profile")}
 className="mb-6"
 >
 <ArrowLeft className="w-4 h-4 mr-2" />
 Back to Profile
 </Button>
 <h1 className="text-3xl md:text-4xl font-bold mb-2">Edit Profile</h1>
 <p className="text-muted-foreground">Update your personal information</p>
 </div>

 {error && (
 <Alert variant="destructive" className="mb-6">
 <AlertDescription>{error}</AlertDescription>
 </Alert>
 )}

 {success && (
 <Alert className="mb-6 border-green-500 bg-green-500/100/10 text-green-600">
 <AlertDescription>{success}</AlertDescription>
 </Alert>
 )}

 <Card>
 <CardHeader>
 <CardTitle>Personal Information</CardTitle>
 </CardHeader>
 <CardContent>
 <form onSubmit={handleSubmit} className="space-y-6">
 <div className="space-y-2">
 <Label htmlFor="fullName">Full Name</Label>
 <div className="relative">
 <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
 <Input
 id="fullName"
 value={formData.fullName}
 onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
 placeholder="Enter your full name"
 className="pl-10"
 />
 </div>
 </div>

 <div className="space-y-2">
 <Label htmlFor="email">Email Address</Label>
 <div className="relative">
 <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
 <Input
 id="email"
 type="email"
 value={formData.email}
 onChange={(e) => setFormData({ ...formData, email: e.target.value })}
 placeholder="Enter your email"
 required
 className="pl-10"
 />
 </div>
 </div>

 <div className="flex flex-col sm:flex-row gap-3 pt-4">
 <Button
 type="submit"
 disabled={saving}
 className="flex-1"
 >
 {saving ? (
 <>
 <Save className="w-4 h-4 mr-2 animate-spin" />
 Saving Changes...
 </>
 ) : (
 <>
 <Save className="w-4 h-4 mr-2" />
 Save Changes
 </>
 )}
 </Button>
 <Button
 type="button"
 variant="outline"
 onClick={() => navigate("/admin/profile")}
 disabled={saving}
 className="flex-1"
 >
 Cancel
 </Button>
 </div>
 </form>
 </CardContent>
 </Card>

 {/* Note about password for non-Telegram users */}
 {!isTelegramUser && (
 <Card className="mt-6">
 <CardHeader>
 <CardTitle>Password</CardTitle>
 </CardHeader>
 <CardContent>
 <p className="text-sm text-muted-foreground mb-4">
 To change your password, please contact support or use the password reset feature.
 </p>
 </CardContent>
 </Card>
 )}
 </div>
 </div>
 );
}
